import React, { useState } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { Bell, X, CheckCircle, AlertCircle, Info, Package, Clock } from 'lucide-react';

const NotificationPanel = () => {
  const { notifications, isConnected, clearNotifications, removeNotification } = useSocket();
  const [isOpen, setIsOpen] = useState(false);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new-order':
        return <Package className="h-5 w-5 text-green-600" />;
      case 'order-status-update':
        return <CheckCircle className="h-5 w-5 text-blue-600" />;
      default:
        return <Info className="h-5 w-5 text-gray-600" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'new-order':
        return 'border-green-200 bg-green-50';
      case 'order-status-update':
        return 'border-blue-200 bg-blue-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-secondary-600 hover:text-secondary-900 transition-colors"
      >
        <Bell className="h-5 w-5 sm:h-6 sm:w-6" />
        {notifications.length > 0 && (
          <span className="absolute -top-1 -right-1 h-4 w-4 sm:h-5 sm:w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {notifications.length > 9 ? '9+' : notifications.length}
          </span>
        )}
        {!isConnected && (
          <span className="absolute -bottom-1 -right-1 h-2 w-2 sm:h-3 sm:w-3 bg-yellow-500 rounded-full border-2 border-white"></span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 sm:w-80 bg-white rounded-lg shadow-lg border border-secondary-200 z-50">
          <div className="p-3 sm:p-4 border-b border-secondary-200">
            <div className="flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-semibold text-secondary-900">Notifications</h3>
              <div className="flex items-center space-x-2">
                {!isConnected && (
                  <div className="flex items-center text-yellow-600 text-sm">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    Disconnected
                  </div>
                )}
                {isConnected && (
                  <div className="flex items-center text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Connected
                  </div>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={clearNotifications}
                    className="text-secondary-500 hover:text-secondary-700 text-sm"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="max-h-80 sm:max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 sm:p-6 text-center text-secondary-500">
                <Bell className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3 text-secondary-300" />
                <p className="text-sm sm:text-base">No notifications yet</p>
                <p className="text-xs sm:text-sm">New orders and updates will appear here</p>
              </div>
            ) : (
              <div className="p-2">
                {notifications.map((notification, index) => (
                  <div
                    key={index}
                    className={`p-2 sm:p-3 mb-2 rounded-lg border ${getNotificationColor(notification.type)} hover:shadow-sm transition-shadow`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        {getNotificationIcon(notification.type)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-secondary-900">
                            {notification.message}
                          </p>
                          {notification.order && (
                            <div className="mt-1 text-xs text-secondary-600">
                              <p>Order #{notification.order.id}</p>
                              <p>${notification.order.total}</p>
                              {notification.order.orderItems && notification.order.orderItems.length > 0 && (
                                <div className="mt-1">
                                  <p className="font-medium">Items:</p>
                                                                     {notification.order.orderItems.slice(0, 3).map((item, idx) => (
                                     <p key={idx} className="text-xs">
                                       {item.quantity}x {item.menuItem?.name || 'Unknown Item'}
                                       {item.addons && item.addons.length > 0 && (
                                         <span className="text-secondary-500"> (+{item.addons.length} addons)</span>
                                       )}
                                     </p>
                                   ))}
                                  {notification.order.orderItems.length > 3 && (
                                    <p className="text-xs text-secondary-500">
                                      +{notification.order.orderItems.length - 3} more items
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          {notification.status && (
                            <div className="mt-1 text-xs text-secondary-600">
                              <p>Status: {notification.status}</p>
                            </div>
                          )}
                          <p className="text-xs text-secondary-500 mt-1">
                            {formatTime(notification.timestamp)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeNotification(index)}
                        className="text-secondary-400 hover:text-secondary-600 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-2 sm:p-3 border-t border-secondary-200 bg-secondary-50">
              <div className="flex items-center justify-between text-xs sm:text-sm text-secondary-600">
                <span>{notifications.length} notification{notifications.length !== 1 ? 's' : ''}</span>
                <button
                  onClick={clearNotifications}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Clear all
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default NotificationPanel; 